import React, { useEffect, useState } from 'react';
import { any, string } from 'prop-types';

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import difference from 'lodash/difference';
import mapValues from 'lodash/mapValues';
import moment from 'moment';

// ✅ NEW (cookies)
import { ConsentProvider, ConsentBanner, useConsent } from 'react-hook-consent';
import './styles/cookieStyle.css';
import ConsentOpenBridge from './components/ConsentOpenBridge/ConsentOpenBridge';
import { initializeHotjar } from './analytics/hotjar';

// Configs and store setup
import defaultConfig from './config/configDefault';
import appSettings from './config/settings';
import configureStore from './store';

// utils
import { RouteConfigurationProvider } from './context/routeConfigurationContext';
import { ConfigurationProvider, useConfiguration } from './context/configurationContext';
import { mergeConfig } from './util/configHelpers';
import { IntlProvider } from './util/reactIntl';
import { includeCSSProperties } from './util/style';
import { IncludeScripts } from './util/includeScripts';

import { LineConnectModalProvider, MaintenanceMode } from './components';

// routing
import routeConfiguration from './routing/routeConfiguration';
import Routes from './routing/Routes';

// translations (✅ ONLY EN + TH)
import defaultMessages from './translations/en.json';
import thMessages from './translations/th.json';

// moment locales (✅ ONLY EN + TH)
import 'moment/locale/en-gb';
import 'moment/locale/th';

// language helpers
import {
  detectBrowserLocale,
  getLocaleFromPathname,
  getLocaleFromSearchParams,
  getLocaleFromCurrentUser,
  getLocaleFromLocalStorage,
  LOCALE_CHANGE_EVENT,
  LOCALE_STORAGE_KEY,
  normalizeLocale,
  resolveLocale,
} from './util/genericHelpers';
import { updateProfile } from './containers/ProfileSettingsPage/ProfileSettingsPage.duck';

const messagesByLocale = {
  en: defaultMessages,
  th: thMessages,
};

const addMissingTranslations = (source, target) => {
  const missingKeys = difference(Object.keys(source), Object.keys(target));
  return missingKeys.reduce((res, key) => ({ ...res, [key]: source[key] }), target);
};

const isTestEnv = process.env.NODE_ENV === 'test';

const getMessagesForLocale = locale => {
  const key = (locale || 'en').toLowerCase();
  return messagesByLocale[key] || defaultMessages;
};

/**
 * ClientOnly component for client-side rendering (SSR-safe for ConsentBanner modal)
 */
const ClientOnly = ({ children }) => {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? children : null;
};

/**
 * ✅ Language provider (FIXED)
 *
 * Canonical sources:
 * - localStorage key: "locale"
 * - user profile key: currentUser.profile.privateData.selectedLanguage
 *
 * Priority:
 * - Logged-in: localStorage > userProfile > detected > 'en'
 * - Logged-out: localStorage > detected > 'en'
 *
 * Also keeps user profile in sync with localStorage once logged in.
 */
const parseUrlParts = url => {
  if (!url) return { pathname: '', search: '' };
  try {
    const parsed = new URL(url, 'http://placeholder.local');
    return { pathname: parsed.pathname, search: parsed.search };
  } catch (e) {
    return { pathname: '', search: '' };
  }
};

// localStorage and browser detection are unavailable during SSR. Use only
// sources that both server and browser can see for the hydration render, then
// resolve the persisted client locale immediately after mounting.
const resolveHydrationLocale = ({ config, pathname, search }) => {
  const urlLocale = getLocaleFromSearchParams(search) || getLocaleFromPathname(pathname);
  const configLocale = config?.localization?.locale;

  return normalizeLocale(urlLocale || configLocale || 'en');
};

const IntlFromUser = ({ children, url }) => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const currentUser = useSelector(s => s.user?.currentUser);

  const isLoggedIn = !!currentUser?.id;

  // --- Read sources (SSR-safe) ---
  const savedLocale = getLocaleFromCurrentUser(currentUser); // should read privateData.selectedLanguage
  const urlParts =
    typeof window !== 'undefined'
      ? { pathname: window.location?.pathname || '', search: window.location?.search || '' }
      : parseUrlParts(url);

  const [locale, setLocale] = useState(() =>
    resolveHydrationLocale({
      config,
      pathname: urlParts.pathname,
      search: urlParts.search,
    })
  );
  const [clientLocaleReady, setClientLocaleReady] = useState(false);

  // Resolve persisted/browser-only sources after hydration. Changing the
  // IntlProvider key remounts the active route, so its localized data loader
  // runs again without a browser reload.
  useEffect(() => {
    const resolvedLocale = resolveLocale({
      currentUser,
      config,
      locationSearch: urlParts.search,
      locationPathname: urlParts.pathname,
    });
    setLocale(resolvedLocale);
    setClientLocaleReady(true);
  }, [config, currentUser, savedLocale, urlParts.pathname, urlParts.search]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleLocaleChange = event => {
      const nextLocale = event?.detail?.locale || getLocaleFromLocalStorage();
      if (nextLocale) {
        setLocale(normalizeLocale(nextLocale));
        setClientLocaleReady(true);
      }
    };
    const handleStorage = event => {
      if (event.key === LOCALE_STORAGE_KEY && event.newValue) {
        setLocale(normalizeLocale(event.newValue));
        setClientLocaleReady(true);
      }
    };

    window.addEventListener(LOCALE_CHANGE_EVENT, handleLocaleChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(LOCALE_CHANGE_EVENT, handleLocaleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Keep localStorage in sync for logged-out navigation
  useEffect(() => {
    if (typeof window === 'undefined' || !clientLocaleReady) return;
    const normalizedLocale = normalizeLocale(locale);
    const stored = getLocaleFromLocalStorage();
    if (stored !== normalizedLocale) {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, normalizedLocale);
    }
  }, [clientLocaleReady, locale]);

  // --- Keep user profile in sync (logged-in only) ---
  // If user is logged in and localStorage differs from profile, persist it.
  useEffect(() => {
    if (!clientLocaleReady) return;
    if (!isLoggedIn) return;
    if (!locale) return;

    const normalizedLocale = normalizeLocale(locale);
    const normalizedSaved = (savedLocale || '').toLowerCase();

    // If profile already matches, do nothing
    if (normalizedSaved === normalizedLocale) return;

    // Persist to profile (privateData.selectedLanguage)
    dispatch(
      updateProfile({
        privateData: {
          ...(currentUser?.attributes?.profile?.privateData || {}),
          selectedLanguage: normalizedLocale,
        },
      })
    );
  }, [clientLocaleReady, dispatch, isLoggedIn, currentUser?.id, locale]); // intentionally NOT depending on savedLocale

  // --- Ensure moment locale matches ---
  useEffect(() => {
    const momentLocale = normalizeLocale(locale) === 'th' ? 'th' : 'en-gb';
    moment.locale(momentLocale);
  }, [locale]);

  // --- Messages ---
  const baseMessages = defaultMessages;
  const localeMessages = isTestEnv
    ? mapValues(baseMessages, (val, key) => key)
    : addMissingTranslations(baseMessages, getMessagesForLocale(normalizeLocale(locale)));

  return (
    <IntlProvider
      key={normalizeLocale(locale)}
      locale={normalizeLocale(locale)}
      messages={localeMessages}
      textComponent="span"
    >
      {children}
    </IntlProvider>
  );
};

/* ------------------------------------------------------------------ */
/* ✅ Cookie Consent (EN + TH)                                          */
/* ------------------------------------------------------------------ */

const consentTranslations = {
  necessaryCookies: { en: 'Necessary', th: 'จำเป็น' },
  preferencesCookies: { en: 'Preferences', th: 'การตั้งค่า' },
  statisticsCookies: { en: 'Statistics', th: 'สถิติ' },
  analyticsCookies: { en: 'Analytics', th: 'วิเคราะห์' },
  marketingCookies: { en: 'Marketing', th: 'การตลาด' },

  Approve: { en: 'Approve', th: 'ยอมรับ' },
  Decline: { en: 'Decline', th: 'ปฏิเสธ' },
  Settings: { en: 'Settings', th: 'ตั้งค่า' },

  CookieMessage: {
    en: `We use cookies and similar technologies to provide, improve, and secure our services. Cookies are small text files stored on your device that help us understand how you use our platform. Cookie Duration: Persistent cookies remain on your device for up to 12 months from your last visit, unless you delete them earlier.`,
    th: `เราใช้คุกกี้และเทคโนโลยีที่คล้ายกันเพื่อให้บริการ ปรับปรุง และรักษาความปลอดภัยให้กับบริการของเรา คุกกี้คือไฟล์ข้อความขนาดเล็กที่ถูกจัดเก็บไว้บนอุปกรณ์ของคุณเพื่อช่วยให้เราเข้าใจการใช้งานแพลตฟอร์มของคุณ ระยะเวลาคุกกี้: คุกกี้แบบถาวร (persistent) จะถูกเก็บไว้ได้นานสูงสุด 12 เดือนนับจากการเข้าชมครั้งล่าสุด เว้นแต่คุณจะลบออกก่อน`,
  },

  necessaryDescription: {
    en:
      'These cookies are essential for the platform to function properly. They enable core features like secure login, account authentication, session management, and access to protected areas of the website.',
    th:
      'คุกกี้เหล่านี้จำเป็นต่อการทำงานของแพลตฟอร์ม ช่วยให้ฟีเจอร์หลักทำงานได้ เช่น การเข้าสู่ระบบอย่างปลอดภัย การยืนยันตัวตน การจัดการเซสชัน และการเข้าถึงพื้นที่ที่ต้องได้รับอนุญาต',
  },

  preferencesDescription: {
    en:
      'Preference cookies remember your choices and settings to provide a more personalised experience. Examples: Language preference, display settings, timezone, accessibility preferences.',
    th:
      'คุกกี้การตั้งค่าจะจดจำตัวเลือกและการตั้งค่าของคุณเพื่อประสบการณ์ที่เป็นส่วนตัวมากขึ้น เช่น ภาษา การแสดงผล โซนเวลา และการช่วยการเข้าถึง',
  },

  statisticsDescription: {
    en:
      'Statistics cookies help us understand how visitors interact with our platform by collecting and reporting anonymous usage information.',
    th:
      'คุกกี้สถิติช่วยให้เราเข้าใจวิธีที่ผู้เยี่ยมชมใช้งานแพลตฟอร์ม โดยเก็บและรายงานข้อมูลการใช้งานแบบไม่ระบุตัวตน',
  },

  analyticsDescription: {
    en:
      'Analytics cookies collect information about how you use our platform to help us improve services and user experience.',
    th: 'คุกกี้วิเคราะห์เก็บข้อมูลการใช้งานเพื่อช่วยให้เราปรับปรุงบริการและประสบการณ์ผู้ใช้',
  },

  marketingDescription: {
    en:
      'Marketing cookies track your activity to deliver relevant advertisements and measure the effectiveness of campaigns.',
    th: 'คุกกี้การตลาดติดตามกิจกรรมเพื่อแสดงโฆษณาที่เกี่ยวข้องและวัดผลแคมเปญการตลาด',
  },

  ModalContent: {
    title: { en: 'Consent settings', th: 'การตั้งค่าความยินยอม' },
    description: {
      en: `We use cookies and similar technologies to provide, improve, and secure our services. Cookies are small text files stored on your device that help us understand how you use our platform.\nCookie Duration: Persistent cookies remain on your device for up to 12 months from your last visit, unless you delete them earlier.`,
      th: `เราใช้คุกกี้และเทคโนโลยีที่คล้ายกันเพื่อให้บริการ ปรับปรุง และรักษาความปลอดภัยให้กับบริการของเรา คุกกี้คือไฟล์ข้อความขนาดเล็กที่ถูกจัดเก็บไว้บนอุปกรณ์ของคุณเพื่อช่วยให้เราเข้าใจการใช้งานแพลตฟอร์มของคุณ\nระยะเวลาคุกกี้: คุกกี้แบบถาวร (persistent) จะถูกเก็บไว้ได้นานสูงสุด 12 เดือนนับจากการเข้าชมครั้งล่าสุด เว้นแต่คุณจะลบออกก่อน`,
    },
    decline: { label: { en: 'Decline', th: 'ปฏิเสธ' } },
    approve: { label: { en: 'Approve selection', th: 'ยอมรับที่เลือก' } },
    approveAll: { label: { en: 'Approve all', th: 'ยอมรับทั้งหมด' } },
  },
};

// Translation helper (consent)
const ct = (obj, locale) => obj?.[locale] ?? obj?.en ?? '';

/* ------------------------------------------------------------------ */

const Configurations = props => {
  const { appConfig, children } = props;
  const routeConfig = routeConfiguration(appConfig.layout, appConfig?.accessControl);

  return (
    <ConfigurationProvider value={appConfig}>
      <RouteConfigurationProvider value={routeConfig}>{children}</RouteConfigurationProvider>
    </ConfigurationProvider>
  );
};

const MaintenanceModeError = ({ locale, messages, helmetContext }) => (
  <IntlProvider locale={locale} messages={messages} textComponent="span">
    <HelmetProvider context={helmetContext}>
      <MaintenanceMode />
    </HelmetProvider>
  </IntlProvider>
);

/**
 * ✅ STABLE consent options - these NEVER change once set
 * This ensures react-hook-consent doesn't re-initialize consent state
 */
const STABLE_CONSENT_OPTIONS = {
  services: [
    {
      id: 'necessary',
      name: 'Necessary',
      description: 'Essential cookies for platform functionality.',
      mandatory: true,
      cookies: [{ pattern: 'necessary_*' }],
    },
    {
      id: 'preferences',
      name: 'Preferences',
      description: 'Cookies that remember your settings.',
      cookies: [{ pattern: 'pref_*' }],
    },
    {
      id: 'statistics',
      name: 'Statistics',
      description: 'Cookies for anonymous usage data.',
      cookies: [{ pattern: 'stats_*' }],
    },
    {
      id: 'analytics',
      name: 'Analytics',
      description: 'Cookies for platform improvement.',
      cookies: [{ pattern: 'analytics_*' }],
    },
    {
      id: 'marketing',
      name: 'Marketing',
      description: 'Cookies for relevant advertisements.',
      cookies: [{ pattern: 'marketing_*' }],
    },
  ],
  theme: 'dark',
};

/**
 * ✅ Locale-aware consent banner (separate from provider)
 * This updates UI labels without affecting consent state
 */
const LocaleAwareConsentBanner = () => {
  const [currentLocale, setCurrentLocale] = useState(() => {
    const stored = getLocaleFromLocalStorage();
    const detected = detectBrowserLocale();
    return stored || detected || 'en';
  });

  // Listen for locale changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkLocale = () => {
      const stored = getLocaleFromLocalStorage();
      if (stored && stored !== currentLocale) {
        setCurrentLocale(stored);
      }
    };

    const interval = setInterval(checkLocale, 500);
    const handleStorage = e => {
      if (e.key === 'locale' && e.newValue) {
        setCurrentLocale(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorage);
    };
  }, [currentLocale]);

  const finalLocale = (currentLocale || 'en').toLowerCase() === 'th' ? 'th' : 'en';

  return (
    <>
      <ConsentBanner
        settings={{
          hidden: false,
          label: ct(consentTranslations.Settings, finalLocale),
          modal: {
            title: ct(consentTranslations.ModalContent.title, finalLocale),
            description: ct(consentTranslations.ModalContent.description, finalLocale),
            decline: {
              label: ct(consentTranslations.ModalContent.decline.label, finalLocale),
            },
            approve: {
              label: ct(consentTranslations.ModalContent.approve.label, finalLocale),
            },
            approveAll: {
              label: ct(consentTranslations.ModalContent.approveAll.label, finalLocale),
            },
          },
        }}
        decline={{
          show: true,
          label: ct(consentTranslations.Decline, finalLocale),
        }}
        approve={{
          label: ct(consentTranslations.Approve, finalLocale),
        }}
      >
        <div>{ct(consentTranslations.CookieMessage, finalLocale)}</div>
      </ConsentBanner>

      <ConsentOpenBridge />
    </>
  );
};

const hasHotjarConsent = hasConsent => {
  if (typeof hasConsent !== 'function') {
    return true;
  }

  // Keep analytics as the primary category while honoring earlier statistics consent values.
  return hasConsent('analytics') || hasConsent('statistics');
};

const HotjarInitializer = () => {
  const { analytics } = useConfiguration();
  const { consent, hasConsent } = useConsent();
  const hotjarConfig = analytics?.hotjar;

  useEffect(() => {
    if (!hotjarConfig) {
      return;
    }

    if (!hasHotjarConsent(hasConsent)) {
      return;
    }

    initializeHotjar(hotjarConfig);
  }, [consent, hasConsent, hotjarConfig]);

  return null;
};

/**
 * ✅ Consent wrapper with STABLE provider
 * ConsentProvider options NEVER change - only the banner labels update
 */
const LocaleAwareConsentWrapper = ({ children }) => {
  return (
    <ConsentProvider options={STABLE_CONSENT_OPTIONS}>
      {/* Root-level initializer keeps Hotjar global for the full SPA. */}
      <HotjarInitializer />
      {children}

      <ClientOnly>
        <LocaleAwareConsentBanner />
      </ClientOnly>
    </ConsentProvider>
  );
};

export const ClientApp = props => {
  const { store, hostedTranslations = {}, hostedConfig = {} } = props;
  const appConfig = mergeConfig(hostedConfig, defaultConfig);

  if (appSettings.dev) {
    const suspiciousKey = Object.keys(process.env || {}).find(
      k => k.startsWith('REACT_APP_') && k.toUpperCase().includes('SECRET')
    );
    if (suspiciousKey) {
      return <div>Unsafe env var: {suspiciousKey}</div>;
    }
  }

  if (!appConfig.hasMandatoryConfigurations) {
    return (
      <MaintenanceModeError
        locale={appConfig.localization.locale}
        messages={{ ...defaultMessages, ...hostedTranslations }}
      />
    );
  }

  includeCSSProperties(appConfig.branding, window.document.documentElement);
  const logLoadDataCalls = appSettings?.env !== 'test';

  return (
    <Configurations appConfig={appConfig}>
      <Provider store={store}>
        <IntlFromUser>
          <HelmetProvider>
            <IncludeScripts config={appConfig} />
            <BrowserRouter>
              <LineConnectModalProvider>
                {/* ✅ Locale-aware consent wrapper handles everything */}
                <LocaleAwareConsentWrapper>
                  <Routes logLoadDataCalls={logLoadDataCalls} />
                </LocaleAwareConsentWrapper>
              </LineConnectModalProvider>
            </BrowserRouter>
          </HelmetProvider>
        </IntlFromUser>
      </Provider>
    </Configurations>
  );
};

ClientApp.propTypes = { store: any.isRequired };

export const ServerApp = props => {
  const { url, context, helmetContext, store, hostedTranslations = {}, hostedConfig = {} } = props;
  const appConfig = mergeConfig(hostedConfig, defaultConfig);
  HelmetProvider.canUseDOM = false;

  if (!appConfig.hasMandatoryConfigurations) {
    return (
      <MaintenanceModeError
        locale={appConfig.localization.locale}
        messages={{ ...defaultMessages, ...hostedTranslations }}
        helmetContext={helmetContext}
      />
    );
  }

  return (
    <Configurations appConfig={appConfig}>
      <Provider store={store}>
        <IntlFromUser url={url}>
          <HelmetProvider context={helmetContext}>
            <IncludeScripts config={appConfig} />
            <StaticRouter location={url} context={context}>
              <LineConnectModalProvider>
                {/* On server we do NOT render ConsentBanner/Bridge (ClientOnly won't run). */}
                <Routes />
              </LineConnectModalProvider>
            </StaticRouter>
          </HelmetProvider>
        </IntlFromUser>
      </Provider>
    </Configurations>
  );
};

ServerApp.propTypes = {
  url: string.isRequired,
  context: any.isRequired,
  store: any.isRequired,
};

export const renderApp = (
  url,
  serverContext,
  preloadedState,
  hostedTranslations,
  hostedConfig,
  collectChunks
) => {
  const store = configureStore(preloadedState);
  const helmetContext = {};

  const WithChunks = collectChunks(
    <ServerApp
      url={url}
      context={serverContext}
      helmetContext={helmetContext}
      store={store}
      hostedTranslations={hostedTranslations}
      hostedConfig={hostedConfig}
    />
  );

  return import('react-dom/server').then(mod => {
    const { default: ReactDOMServer } = mod;
    const body = ReactDOMServer.renderToString(WithChunks);
    const { helmet: head } = helmetContext;
    return { head, body };
  });
};
